import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ArrowRight, Check, Minus, Plus, Sparkles, Table2, X } from 'lucide-react';

interface Table {
  id: string;
  name: string;
  capacity: number;
  zoneName?: string;
}

interface Zone {
  name: string;
  color: string;
}

interface StepTablesProps {
  zones: Zone[];
  onComplete: (data: { tables: Array<{ name: string; capacity: number; zoneName?: string }> }) => void;
  onBack: () => void;
  onSkip: () => void;
}

interface TablePreset {
  id: string;
  name: string;
  icon: string;
  description: string;
  tables: Array<{ name: string; capacity: number }>;
}

const TABLE_PRESETS: TablePreset[] = [
  {
    id: 'small',
    name: 'Petit établissement',
    icon: '🏠',
    description: '5 tables de 4 personnes',
    tables: Array.from({ length: 5 }, (_, i) => ({ 
      name: `Table ${i + 1}`, 
      capacity: 4 
    })),
  },
  {
    id: 'medium',
    name: 'Établissement moyen',
    icon: '🏢',
    description: '10 tables de 4 personnes',
    tables: Array.from({ length: 10 }, (_, i) => ({ 
      name: `Table ${i + 1}`, 
      capacity: 4 
    })),
  },
  {
    id: 'large',
    name: 'Grande capacité',
    icon: '🏛️',
    description: '15 tables (mix 2-6 personnes)',
    tables: [
      ...Array.from({ length: 5 }, (_, i) => ({ name: `Table ${i + 1}`, capacity: 2 })),
      ...Array.from({ length: 5 }, (_, i) => ({ name: `Table ${i + 6}`, capacity: 4 })),
      ...Array.from({ length: 5 }, (_, i) => ({ name: `Table ${i + 11}`, capacity: 6 })),
    ],
  },
];

export function StepTables({ zones, onComplete, onBack, onSkip }: StepTablesProps) {
  const [tables, setTables] = useState<Table[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  
  // Custom table form
  const [tableName, setTableName] = useState('');
  const [tableCapacity, setTableCapacity] = useState(4);
  const [selectedZone, setSelectedZone] = useState<string>('none');

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const getNextTableNumber = () => {
    const numbers = tables
      .map(t => {
        const match = t.name.match(/^Table (\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter(n => n > 0);
    
    if (numbers.length === 0) return 1;
    return Math.max(...numbers) + 1;
  };

  const applyPreset = (preset: TablePreset) => {
    if (selectedPreset === preset.id) {
      // Deselect preset and clear tables
      setSelectedPreset(null);
      setTables([]);
      return;
    }

    const newTables = preset.tables.map((t, index) => {
      // Distribute tables evenly across available zones
      const zoneIndex = zones.length > 0 ? index % zones.length : -1;
      return {
        id: generateId(),
        name: t.name,
        capacity: t.capacity,
        zoneName: zoneIndex >= 0 ? zones[zoneIndex].name : undefined,
      };
    });
    
    setTables(newTables);
    setSelectedPreset(preset.id);
  };

  const handleAddTable = () => {
    const name = tableName.trim() || `Table ${getNextTableNumber()}`;
    
    const newTable: Table = {
      id: generateId(),
      name,
      capacity: tableCapacity,
      zoneName: selectedZone !== 'none' ? selectedZone : undefined,
    };
    
    setTables([...tables, newTable]);
    setTableName('');
    setSelectedPreset(null); // Clear preset selection when adding custom table
  };

  const handleRemoveTable = (id: string) => {
    setTables(tables.filter(t => t.id !== id));
    setSelectedPreset(null); // Clear preset selection when removing table
  };

  const handleSubmit = () => {
    onComplete({
      tables: tables.map(t => ({
        name: t.name,
        capacity: t.capacity,
        zoneName: t.zoneName,
      })),
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTable();
    }
  };

  const getZoneColor = (zoneName: string) => {
    const zone = zones.find(z => z.name === zoneName);
    return zone?.color;
  };

  // Group tables by zone for display
  const tablesByZone = tables.reduce((acc, table) => {
    const zone = table.zoneName || 'Sans zone';
    if (!acc[zone]) acc[zone] = [];
    acc[zone].push(table);
    return acc;
  }, {} as Record<string, Table[]>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Table2 className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">Vos tables</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choisissez une configuration de départ
        </p>
      </div>

      {/* Preset configurations */}
      <div className="space-y-3">
        <Label>Configurations suggérées :</Label>
        <div className="grid grid-cols-1 gap-2">
          {TABLE_PRESETS.map((preset) => {
            const isSelected = selectedPreset === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  isSelected 
                    ? 'border-primary bg-primary/10' 
                    : 'border-muted hover:border-primary/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{preset.icon}</span>
                    <div>
                      <span className="font-medium">{preset.name}</span>
                      <p className="text-sm text-muted-foreground">{preset.description}</p>
                    </div>
                  </div>
                  {isSelected && <Check className="w-5 h-5 text-primary" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tables list */}
      {tables.length > 0 && (
        <div className="space-y-3">
          <Label>Tables créées</Label>
          <div className="bg-muted/30 rounded-lg p-3 space-y-3 max-h-[200px] overflow-y-auto">
            {Object.entries(tablesByZone).map(([zoneName, zoneTables]) => (
              <div key={zoneName} className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {zoneName !== 'Sans zone' && (
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getZoneColor(zoneName) }}
                    />
                  )}
                  <span className="font-medium">{zoneName}</span>
                  <span>({zoneTables.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {zoneTables.map((table) => (
                    <div
                      key={table.id}
                      className="flex items-center gap-1 bg-background rounded-md px-2 py-1 border text-sm"
                    >
                      <span className="font-medium">{table.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {table.capacity}p
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveTable(table.id)}
                        className="h-5 w-5 text-muted-foreground hover:text-destructive ml-1"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom table form */}
      <div className="space-y-3 pt-4 border-t">
        <Label className="text-muted-foreground text-sm">Personnaliser (optionnel)</Label>
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-1">
            <Input
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Table ${getNextTableNumber()}`}
              className="h-9"
            />
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setTableCapacity(Math.max(1, tableCapacity - 1))}
              disabled={tableCapacity <= 1}
              className="h-9 w-9"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="text-sm font-bold w-8 text-center">{tableCapacity}p</span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setTableCapacity(Math.min(20, tableCapacity + 1))}
              disabled={tableCapacity >= 20}
              className="h-9 w-9"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {zones.length > 0 && (
            <Select value={selectedZone} onValueChange={setSelectedZone}>
              <SelectTrigger className="w-[130px] h-9">
                <SelectValue placeholder="Zone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sans zone</SelectItem>
                {zones.map((zone) => (
                  <SelectItem key={zone.name} value={zone.name}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: zone.color }}
                      />
                      {zone.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button onClick={handleAddTable} variant="outline" size="icon" className="h-9 w-9">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Info about assisted configuration */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium">
              Besoin d'aide pour configurer votre salle ?
            </p>
            <p className="text-xs text-muted-foreground">
              Notre équipe peut configurer votre plan de salle <strong>gratuitement</strong> à partir 
              d'une simple photo ou schéma. Vous pourrez demander cette aide depuis 
              la page "Plan de salle" après la configuration.
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <Button variant="ghost" onClick={onSkip} className="flex-1">
          Passer
        </Button>
        <Button onClick={handleSubmit} className="flex-1">
          Continuer
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
